import React, { useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import PaymentCoupons from "app/pages/Booking/Payment/PaymentCoupons/PaymentCoupons";
import { isAuthenticated, isOperatorMode } from "app/pages/Auth/authSelectors";
import {
	fetchUsableCoupons,
	fetchUsableCreditNotes,
} from "app/pages/Account/MyCoupons/couponActionCreators";
import {
	getOrderedUsableCredits,
	getOrderedUnusableCredits,
	getUsableCreditsTotal,
	getUsableCreditNotesTotal,
	getOnlyNonApplicableCredits,
	getOrderedUsableCreditNotes,
	getOrderedUnusableCreditNotes,
	getOnlyNonApplicableCreditNotes,
} from "app/pages/Account/MyCoupons/couponSelector";
import { PAYMENT_COUPON_TYPE } from "app/constants";
import { updateBooking } from "app/pages/Booking/bookingActionCreators";
import { sendTagOnCouponChange } from "app/utils/analytics";

const PaymentCouponsContainer = props => {
	const {
		fetchUsableCoupons,
		fetchUsableCreditNotes,
		quotationCode,
		updateBooking,
		promotionCode,
		insuranceCode,
		couponType,
		isAuthenticated,
		enableCredits,
		enableCreditNotes,
	} = props;

	useEffect(() => {
		if (isAuthenticated && (enableCredits || enableCreditNotes)) {
			fetchUsableCoupons({
				connectivitySessionCode: quotationCode,
				isPromoCode: couponType === PAYMENT_COUPON_TYPE.PROMOCODE,
				promotionCode: promotionCode,
				insuranceCode,
			});
		}
	}, [quotationCode, insuranceCode, isAuthenticated, enableCredits, enableCreditNotes]);

	const onCouponChange = useCallback(
		value => {
			updateBooking({
				coupon: value,
			});

			fetchUsableCreditNotes({
				connectivitySessionCode: quotationCode,
				isPromoCode: value === PAYMENT_COUPON_TYPE.PROMOCODE,
				promotionCode: promotionCode,
				insuranceCode,
			});

			sendTagOnCouponChange(value);
		},
		[quotationCode, insuranceCode, promotionCode]
	);

	const forcePromoCodeSelection = useCallback(
		codePromo => {
			if (codePromo) {
				updateBooking({
					coupon: PAYMENT_COUPON_TYPE.PROMOCODE,
				});
			}

			if (isAuthenticated) {
				fetchUsableCreditNotes({
					connectivitySessionCode: quotationCode,
					isPromoCode: couponType === PAYMENT_COUPON_TYPE.PROMOCODE,
					promotionCode: codePromo,
					insuranceCode,
				});
			}
		},
		[couponType, insuranceCode, quotationCode]
	);

	return (
		<PaymentCoupons
			{...props}
			onChange={onCouponChange}
			onValidPromoCodeApplied={forcePromoCodeSelection}
			onPromoCodeFocus={forcePromoCodeSelection}
		/>
	);
};

PaymentCouponsContainer.propTypes = {
	field: PropTypes.object,
	usableCouponsStatus: PropTypes.string,
	fetchUsableCoupons: PropTypes.func,
	fetchUsableCreditNotes: PropTypes.func,
	updateBooking: PropTypes.func,
	totalUsableCredit: PropTypes.number,
	enablePromotionCode: PropTypes.bool,
	quotationCode: PropTypes.string,
	promotionCode: PropTypes.string,
	insuranceCode: PropTypes.string,
	couponType: PropTypes.string,
	isAuthenticated: PropTypes.bool,
	enableCredits: PropTypes.bool,
	enableCreditNotes: PropTypes.bool,
};

const mapStateToProps = state => {
	const usableCouponsStatus = state.usableCoupons.status;
	const totalUsableCredit = getUsableCreditsTotal(state);
	const enablePromotionCode = isOperatorMode(state) || state.partner.enablePromotionCode;
	return {
		enablePromotionCode,
		enableCredits: state.partner.enableCredits,
		enableCreditNotes: state.partner.enableCreditNotes,
		quotationCode: state.quotation.code,
		usableCredits: getOrderedUsableCredits(state),
		unusableCredits: getOrderedUnusableCredits(state),
		usableCreditNotes: getOrderedUsableCreditNotes(state),
		unusableCreditNotes: getOrderedUnusableCreditNotes(state),
		onlyNonApplicableCredits: getOnlyNonApplicableCredits(state),
		onlyNonApplicableCreditNotes: getOnlyNonApplicableCreditNotes(state),
		totalUsableCredit,
		totalUsableCreditNote: getUsableCreditNotesTotal(state),
		couponType: state.booking.coupon,
		usableCouponsStatus,
		promotionValue: state.promotion.promotionValue,
		promotionCode: state.promotion.promotionCode,
		insuranceCode: state.booking.insurance.code,
		isAuthenticated: isAuthenticated(state),
	};
};

const mapDispatchToProps = dispatch => {
	return bindActionCreators(
		{
			fetchUsableCoupons,
			fetchUsableCreditNotes,
			updateBooking,
		},
		dispatch
	);
};

export default connect(
	mapStateToProps,
	mapDispatchToProps
)(PaymentCouponsContainer);
